import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { Loader2, Save, LayoutDashboard, Check, GripVertical } from "lucide-react";
import { DragDropContext, Droppable, Draggable } from "@hello-pangea/dnd";
import {
  WIDGET_CATALOG, WIDGET_ORDER, WIDGET_GROUP_ORDER, DASHBOARD_PRESETS,
  presetEnabledMap, resolveVisibleWidgets, normalizeConfig, DEFAULT_DASHBOARD_CONFIG, recommendedPreset, defaultPresetFor,
} from "@/lib/dashboardWidgets";

const SPAN_CLS = { 1: "col-span-1", 2: "col-span-2", 4: "col-span-2" };
const DENSITY = [["rahat", "Rahat"], ["kompakt", "Kompakt"]];
const CARD_STYLE = [["golgeli", "Gölgeli"], ["cizgili", "Çizgili"], ["duz", "Düz"]];

export default function DashboardDesign({ onSaved } = {}) {
  const { user, activeSector, activeSectorObj } = useAuth();
  const recPreset = activeSector ? recommendedPreset(activeSector) : null;
  const [cfg, setCfg] = useState(DEFAULT_DASHBOARD_CONFIG);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    api.get("/settings/dashboard-config")
      .then((r) => { const raw = r.data; setCfg(raw && raw.preset ? normalizeConfig(raw) : normalizeConfig({ preset: defaultPresetFor(user?.role, activeSector) })); })
      .catch(() => setCfg(normalizeConfig({ preset: defaultPresetFor(user?.role, activeSector) })))
      .finally(() => setLoading(false));
  }, [activeSector, user?.role]);

  const selectPreset = (id) => setCfg((c) => ({ ...c, preset: id, enabled: presetEnabledMap(id), order: [] }));
  const toggleWidget = (key) => setCfg((c) => ({ ...c, preset: "custom", enabled: { ...c.enabled, [key]: !c.enabled?.[key] } }));
  const onDragEnd = (result) => {
    if (!result.destination || result.destination.index === result.source.index) return;
    const items = Array.from(visible);
    const [moved] = items.splice(result.source.index, 1);
    items.splice(result.destination.index, 0, moved);
    setCfg((c) => {
      const enabled = c.preset === "custom" ? c.enabled : presetEnabledMap(c.preset);
      return { ...c, preset: "custom", enabled, order: items };
    });
  };
  const save = async () => {
    setSaving(true);
    try { const { data } = await api.put("/settings/dashboard-config", cfg); setCfg(normalizeConfig(data)); toast.success("Panel tasarımı kaydedildi — panele yansıdı"); onSaved?.(); }
    catch (e) { toast.error("Kaydedilemedi"); }
    setSaving(false);
  };

  const visible = useMemo(() => resolveVisibleWidgets(cfg), [cfg]);
  const activeCount = visible.length;

  if (loading) return <div className="rounded-2xl border border-slate-200 bg-white p-8 text-center text-sm text-slate-400 shadow-sm">Yükleniyor…</div>;

  return (
    <div className="rounded-2xl border border-slate-200 bg-white p-6 shadow-sm" data-testid="dashboard-design">
      <div className="mb-1 flex items-center gap-2">
        <LayoutDashboard className="h-5 w-5 text-brand-green" />
        <h2 className="font-heading text-lg font-bold text-slate-800">Panel Tasarımı</h2>
      </div>
      <p className="mb-5 text-sm text-slate-500">Ana panelinde <b>hangi bileşenlerin</b> ve <b>hangi düzende</b> görüneceğini seç. Hazır bir düzen seç ya da bileşenleri tek tek aç/kapat. Sağda canlı önizleme görürsün.</p>

      <div className="grid gap-6 lg:grid-cols-[minmax(0,420px)_1fr]">
        {/* ---- Sol: yapılandırma ---- */}
        <div className="space-y-6">
          <div>
            <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Hazır Düzen</p>
            {recPreset && cfg.preset !== recPreset && (
              <div className="mb-3 flex flex-wrap items-center justify-between gap-2 rounded-lg bg-emerald-50 p-2.5 text-xs ring-1 ring-inset ring-emerald-200" data-testid="dashboard-sector-reco">
                <span className="text-emerald-800">Sektörünüz{activeSectorObj?.name ? ` (${activeSectorObj.name})` : ""} için önerilen düzen: <b>{DASHBOARD_PRESETS.find((p) => p.id === recPreset)?.name}</b></span>
                <button type="button" data-testid="dashboard-apply-reco" onClick={() => selectPreset(recPreset)} className="rounded-md bg-emerald-600 px-2.5 py-1 font-semibold text-white transition-colors hover:bg-emerald-700">Öneriyi Uygula</button>
              </div>
            )}
            <div className="grid gap-2 sm:grid-cols-2" data-testid="dashboard-design-presets">
              {DASHBOARD_PRESETS.map((p) => {
                const Icon = p.icon;
                const active = cfg.preset === p.id;
                return (
                  <button key={p.id} type="button" data-testid={`dashboard-preset-${p.id}`} onClick={() => selectPreset(p.id)}
                    className={`flex items-start gap-2.5 rounded-xl border p-3 text-left transition-colors ${active ? "border-brand-green bg-brand-green/5 ring-2 ring-brand-green/30" : "border-slate-200 hover:border-slate-300"}`}>
                    <div className={`mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${active ? "bg-brand-green text-white" : "bg-slate-100 text-slate-500"}`}><Icon className="h-4 w-4" /></div>
                    <div className="min-w-0">
                      <p className="flex items-center gap-1 text-sm font-bold text-slate-800">{p.name}{active && <Check className="h-3.5 w-3.5 text-brand-green" />}</p>
                      <p className="text-[11px] leading-snug text-slate-400">{p.description}</p>
                    </div>
                  </button>
                );
              })}
              <button type="button" data-testid="dashboard-preset-custom" onClick={() => setCfg((c) => ({ ...c, preset: "custom" }))}
                className={`flex items-center gap-2.5 rounded-xl border border-dashed p-3 text-left transition-colors sm:col-span-2 ${cfg.preset === "custom" ? "border-brand-green bg-brand-green/5 ring-2 ring-brand-green/30" : "border-slate-300 hover:border-slate-400"}`}>
                <div className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-md ${cfg.preset === "custom" ? "bg-brand-green text-white" : "bg-slate-100 text-slate-500"}`}><Check className="h-4 w-4" /></div>
                <div><p className="text-sm font-bold text-slate-800">Özel (Kendin Seç)</p><p className="text-[11px] text-slate-400">Aşağıdan bileşenleri tek tek aç/kapat.</p></div>
              </button>
            </div>
          </div>

          <div>
            <div className="mb-2 flex items-center justify-between">
              <p className="text-xs font-semibold uppercase tracking-wide text-slate-400">Bileşenler ({activeCount} açık)</p>
              {cfg.preset === "custom" && <span className="rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-bold text-amber-700">Özel</span>}
            </div>
            <div className="space-y-3" data-testid="dashboard-design-widgets">
              {WIDGET_GROUP_ORDER.map((grp) => (
                <div key={grp}>
                  <p className="mb-1 text-[10px] font-bold uppercase tracking-widest text-slate-400">{grp}</p>
                  <div className="grid gap-1 sm:grid-cols-2">
                    {WIDGET_ORDER.filter((k) => WIDGET_CATALOG[k].group === grp).map((k) => (
                      <label key={k} className="flex items-center justify-between gap-2 rounded-md px-2.5 py-1.5 ring-1 ring-inset ring-slate-100 hover:bg-slate-50">
                        <span className="text-sm text-slate-700">{WIDGET_CATALOG[k].title}</span>
                        <Switch data-testid={`dashboard-widget-toggle-${k}`} checked={!!cfg.enabled?.[k]} onCheckedChange={() => toggleWidget(k)} />
                      </label>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          </div>

          {visible.length > 0 && (
            <div>
              <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Panel Sırası (sürükle-bırak)</p>
              <DragDropContext onDragEnd={onDragEnd}>
                <Droppable droppableId="dash-order">
                  {(prov) => (
                    <div ref={prov.innerRef} {...prov.droppableProps} className="space-y-1.5" data-testid="dashboard-order-list">
                      {visible.map((k, i) => {
                        const w = WIDGET_CATALOG[k];
                        if (!w) return null;
                        return (
                          <Draggable key={k} draggableId={k} index={i}>
                            {(p, snap) => (
                              <div ref={p.innerRef} {...p.draggableProps} {...p.dragHandleProps}
                                data-testid={`dashboard-order-item-${k}`}
                                className={`flex items-center gap-2 rounded-md border px-2.5 py-2 text-sm ${snap.isDragging ? "border-brand-green bg-white shadow-md ring-2 ring-brand-green/30" : "border-slate-200 bg-white"}`}>
                                <GripVertical className="h-4 w-4 shrink-0 cursor-grab text-slate-400" />
                                <span className="w-5 shrink-0 text-center text-[11px] font-bold text-slate-400">{i + 1}</span>
                                <span className="min-w-0 flex-1 truncate text-slate-700">{w.title}</span>
                              </div>
                            )}
                          </Draggable>
                        );
                      })}
                      {prov.placeholder}
                    </div>
                  )}
                </Droppable>
              </DragDropContext>
              <p className="mt-1.5 text-[11px] text-slate-400">Sıralamayı değiştirmek düzeni <b>Özel</b>'e çevirir; kaydedince panele yansır.</p>
            </div>
          )}

          <div className="grid grid-cols-2 gap-4">
            <div>
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">Yoğunluk</p>
              <div className="inline-flex rounded-md bg-slate-100 p-1" data-testid="dashboard-density">
                {DENSITY.map(([k, l]) => (
                  <button key={k} type="button" data-testid={`dashboard-density-${k}`} onClick={() => setCfg((c) => ({ ...c, density: k }))}
                    className={`rounded-sm px-3 py-1 text-xs font-semibold transition-colors ${cfg.density === k ? "bg-white text-brand-green shadow-sm" : "text-slate-500"}`}>{l}</button>
                ))}
              </div>
            </div>
            <div>
              <p className="mb-1.5 text-xs font-semibold uppercase tracking-wide text-slate-400">Kart Stili</p>
              <div className="inline-flex rounded-md bg-slate-100 p-1" data-testid="dashboard-cardstyle">
                {CARD_STYLE.map(([k, l]) => (
                  <button key={k} type="button" data-testid={`dashboard-cardstyle-${k}`} onClick={() => setCfg((c) => ({ ...c, card_style: k }))}
                    className={`rounded-sm px-3 py-1 text-xs font-semibold transition-colors ${cfg.card_style === k ? "bg-white text-brand-green shadow-sm" : "text-slate-500"}`}>{l}</button>
                ))}
              </div>
            </div>
          </div>

          <Button data-testid="dashboard-design-save" onClick={save} disabled={saving} className="bg-brand-green text-white hover:bg-brand-green-600">
            {saving ? <Loader2 className="mr-1.5 h-4 w-4 animate-spin" /> : <Save className="mr-1.5 h-4 w-4" />} Kaydet
          </Button>
        </div>

        {/* ---- Sağ: canlı önizleme ---- */}
        <div>
          <p className="mb-2 text-xs font-semibold uppercase tracking-wide text-slate-400">Canlı Önizleme</p>
          <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 p-4" data-testid="dashboard-design-preview">
            <div className="mb-3 flex items-center gap-1.5">
              <span className="h-2.5 w-2.5 rounded-full bg-red-400" /><span className="h-2.5 w-2.5 rounded-full bg-amber-400" /><span className="h-2.5 w-2.5 rounded-full bg-emerald-400" />
              <span className="ml-2 text-[10px] font-semibold text-slate-400">Panel · {DASHBOARD_PRESETS.find((p) => p.id === cfg.preset)?.name || "Özel"}</span>
            </div>
            {visible.length === 0 ? (
              <p className="py-12 text-center text-xs text-slate-400">Hiç bileşen seçilmedi. Soldan bir düzen seç veya bileşen aç.</p>
            ) : (
              <div className={`grid grid-cols-4 ${cfg.density === "kompakt" ? "gap-1.5" : "gap-2.5"}`}>
                {visible.map((k) => {
                  const w = WIDGET_CATALOG[k];
                  if (!w) return null;
                  const isKpi = k.startsWith("kpi_");
                  return (
                    <div key={k} className={`${SPAN_CLS[w.span] || "col-span-1"} rounded-lg border p-2 ${cfg.card_style === "duz" ? "border-slate-100 bg-slate-100/70" : "border-slate-200 bg-white shadow-sm"}`} data-testid={`dashboard-preview-${k}`}>
                      <p className="truncate text-[9px] font-bold uppercase tracking-wide text-slate-400">{w.group.split(" ")[0]}</p>
                      <p className="truncate text-[11px] font-semibold text-slate-700">{w.title}</p>
                      {isKpi ? (
                        <p className="mt-1 h-4 w-2/3 rounded bg-brand-green/20" />
                      ) : k.startsWith("chart_") ? (
                        <div className="mt-1 flex h-8 items-end gap-0.5">{[5, 8, 4, 9, 6, 7].map((h, i) => <span key={i} className="flex-1 rounded-sm bg-brand-orange/40" style={{ height: `${h * 3}px` }} />)}</div>
                      ) : (
                        <div className="mt-1 space-y-0.5">{[0, 1, 2].map((i) => <span key={i} className="block h-1.5 rounded bg-slate-200" style={{ width: `${90 - i * 18}%` }} />)}</div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
          <p className="mt-2 text-[11px] text-slate-400">Önizleme yerleşimi ve sırayı gösterir; gerçek panelde canlı verilerle dolar.</p>
        </div>
      </div>
    </div>
  );
}
